import type React from "react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import { Button } from "@/components/ui/button";
import LimitWarning from "@/components/ui/LimitWarning";
import Modal from "@/components/ui/modal";
import { MESSAGE } from "@/constants/message";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { cn } from "@/lib/utils";
import {
  ESidebarMenuType,
  setSideBarMenuTypePopupDisplay,
  toggleDatasetExplorerPopup
} from "@/store/slice/uiSlice";
import type { PostPoiParametersResponse } from "@/types/poiParameter";
import type { AdminAreaSettings } from "@/types/spatial";

import { DEFAULT_SESSION_DATA, SESSION_STORAGE_KEY } from "../constants";
import { DatasetSection } from "./DatasetSection";
import { usePoiConfirmation } from "./usePoiConfirmation";
import { groupDatasetsByStatus } from "./utils";

interface PoiConfirmationViewProps {
  data?: PostPoiParametersResponse;
  open: boolean;
  showPreview: boolean;
  selectedAdminArea: AdminAreaSettings | null;
  selectedAdminAreaIds?: string[];
  selectedDatasetIds?: string[];
  selectedCountry?: {
    label: string;
    value: string;
  } | null;
  isGlobalFilterAreaSelected?: boolean;
  isOverrideGlobalFilter?: boolean;
  onOpenChange: (open: boolean) => void;
  setSelectedDatasetId: (id: string | null) => void;
  setShowPreview: (show: boolean) => void;
  setShowUnlockModal: (show: boolean) => void;
  setSelectedDatasetIds?: (ids: string[]) => void;
}

export const PoiConfirmationView = ({
  data,
  open,
  showPreview,
  selectedAdminArea,
  selectedAdminAreaIds,
  selectedDatasetIds,
  selectedCountry,
  isGlobalFilterAreaSelected,
  isOverrideGlobalFilter,
  onOpenChange,
  setSelectedDatasetId,
  setShowPreview,
  setShowUnlockModal,
  setSelectedDatasetIds
}: PoiConfirmationViewProps) => {
  const { showConfirmation } = useConfirmationModal();
  const { isGlobalFilterOverrideEnabled } = useAppFeatures();
  const [ignoreDataLocked, setIgnoreDataLocked] = useState(false);
  const dispatch = useDispatch();
  const { showLoading, showSuccess, showError } = useLoadingAndToast();
  // Clears the Dataset Explorer's persisted UI state on a successful add, so
  // reopening the explorer starts fresh — mirrors the Data Explore close path
  // (`handleClose` → `removeSessionItem` in the explorer index).
  const { removeSessionItem } = useSessionStorage(
    SESSION_STORAGE_KEY,
    DEFAULT_SESSION_DATA
  );

  // Separate datasets into locked and unlocked
  const { dataSelection, dataAdded, dataLocked } = useMemo(
    () => groupDatasetsByStatus(data?.datasets || []),
    [data?.datasets]
  );

  const adminAreaSettings: AdminAreaSettings = {
    ...selectedAdminArea,
    lv0: {
      label: selectedCountry?.label ?? "",
      value: selectedCountry?.value ?? ""
    }
  };

  // Datasets that will actually be added: the user's pick from each selection
  // group + everything in "Data Added". This is the set the limit is measured
  // against (recomputed as the checklist selection changes).
  const datasetsToAdd = useMemo(() => {
    const selectedFromGroups = dataSelection.flatMap((group) =>
      group.filter((ds) => selectedDatasetIds?.includes(ds.dataset_id))
    );
    return [...selectedFromGroups, ...dataAdded];
  }, [dataSelection, dataAdded, selectedDatasetIds]);

  const {
    handleConfirmDatasets,
    isOverLimit: isInputRowsOverLimit,
    limit: inputRowsLimit,
    isFetchingStatusDataset,
    isPreparingTargets
  } = usePoiConfirmation({
    poiDatasets: datasetsToAdd,
    selectedAdminArea: adminAreaSettings,
    selectedAdminAreaIds,
    enabled: open
  });

  // True while the `/stats` payload is being built or counted.
  const isCheckingLimit = isPreparingTargets || isFetchingStatusDataset;

  // Stop propagation to prevent parent modal's useOnClickOutside from triggering
  const handleMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const processAddDataSetPOIParameter = async (shouldApplyGlobal: boolean) => {
    onOpenChange(false);
    setIgnoreDataLocked(false);
    setSelectedDatasetIds?.([]);

    showLoading(
      <NotificationContent description="Please wait while your dataset is being saved." />
    );

    try {
      await handleConfirmDatasets({
        poiDatasets: datasetsToAdd,
        selectedAdminArea: adminAreaSettings,
        selectedAdminAreaIds,
        shouldApplyGlobal,
        isOverrideGlobalFilter
      });
      const confirmedPoiDatasetNames = datasetsToAdd.map((d) => d.dataset_name);
      const isPlural = confirmedPoiDatasetNames.length > 1;

      dispatch(toggleDatasetExplorerPopup(false));
      dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
      removeSessionItem();
      showSuccess(
        <NotificationContent
          title={`Dataset${isPlural ? "s" : ""} Loaded Successfully`}
        >
          <>
            {`The Dataset${isPlural ? "s" : ""} `}
            <span className="font-semibold">
              {confirmedPoiDatasetNames
                .join(", ")
                .replace(/, ([^,]*)$/, " and $1")}
            </span>{" "}
            {`${isPlural ? "are" : "is"} now ready for analysis and use on the platform`}
          </>
        </NotificationContent>
      );
    } catch {
      showError(
        <NotificationContent
          title="Something Went Wrong"
          description="Sorry, we're experiencing an issue processing your request. Please try again."
        />
      );
    }
  };

  const handleConfirm = async () => {
    if (dataLocked.length > 0 && !ignoreDataLocked) {
      setShowUnlockModal(true);
      onOpenChange(false);
      return;
    }

    if (isGlobalFilterOverrideEnabled && !isGlobalFilterAreaSelected) {
      showConfirmation({
        title: "Confirmation",
        message: MESSAGE.DATASET_CONFIRMATION_CHANGE_GLOBAL_FILTER,
        confirmLabel: "Yes",
        cancelLabel: "No",
        onConfirm: () => {
          processAddDataSetPOIParameter(true);
        },
        onCancel: () => {
          processAddDataSetPOIParameter(false);
        }
      });
      return;
    }

    await processAddDataSetPOIParameter(false);
  };

  const handleCloseModalConfirmation = () => {
    onOpenChange(false);
    setIgnoreDataLocked(false);
    setSelectedDatasetIds?.([]);
  };

  const isNoDataAdded = dataAdded?.length === 0;
  const isNoSelection = dataSelection.length === 0;
  const isNoSelectedDataset = selectedDatasetIds?.length === 0;
  const hasLockedData = dataLocked.length > 0;

  const isDisabled =
    (isNoDataAdded && !isNoSelection && isNoSelectedDataset) ||
    (isNoDataAdded && isNoSelection && hasLockedData && ignoreDataLocked);

  return (
    <Modal
      isOpen={open}
      onClose={handleCloseModalConfirmation}
      containerClassname="w-full !max-w-[480px] p-0 gap-0 overflow-hidden border-none shadow-[0px_4px_24px_5px_rgba(206,206,206,0.4)] rounded-[12px] bg-white"
      shouldCloseModalOnClickOutside={false}
      onMouseUp={handleMouseUp}
      className={cn(showPreview ? "!bg-transparent" : "")}
    >
      <div>
        {/* Header */}
        <div className="px-6 pt-6 pb-4 bg-white flex flex-row items-center justify-between">
          <h2 className="text-[20px] font-semibold leading-[28px] text-[#101010] font-['Plus_Jakarta_Sans']">
            Datasets Confirmation
          </h2>
          <button
            type="button"
            onClick={handleCloseModalConfirmation}
            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <CloseIcon className="w-5 h-5 fill-txtBody" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-6 pb-6 bg-white overflow-y-auto max-h-[70vh] h-full custom-scrollbar">
          {/* Data-rows threshold warning, shown above "Data Selection". */}
          {isInputRowsOverLimit && (
            <LimitWarning
              title={MESSAGE.LIMIT_THRESHOLD_WARNING.TITLE}
              message={MESSAGE.LIMIT_THRESHOLD_WARNING.MESSAGE(
                inputRowsLimit,
                "dataset"
              )}
            />
          )}
          {/* Empty State */}
          {!data?.datasets?.length && (
            <DatasetSection
              title="Data Added"
              description="These datasets will be added to your analysis canvas based on the filters you selected."
              datasets={[]}
              isLockedSection={false}
              emptyStateMessage={
                data?.message?.includes("brand")
                  ? "The data selected is not available because the brand isn’t available in a specific city or country."
                  : "Data selected is not available at the moment. Try to pick other POIs parameter"
              }
            />
          )}
          {/* Data Selection Section */}
          {dataSelection.length > 0 && (
            <DatasetSection
              isLoading={isCheckingLimit}
              title="Data Selection"
              description="We found similar dataset, please pick one which dataset you would to like add. "
              datasets={dataSelection}
              isSelection
              selectedIds={selectedDatasetIds}
              onChange={setSelectedDatasetIds}
            />
          )}
          {/* Data Locked Section */}
          {dataLocked.length > 0 && (
            <DatasetSection
              title="Data Locked"
              description="locked dataset cannot be used, unless you do the purchase"
              datasets={dataLocked}
              isLockedSection={true}
              onClickLockIcon={(dataset) => {
                setSelectedDatasetId(dataset.dataset_id);
                setShowPreview(true);
              }}
            />
          )}
          {/* Data Added Section */}
          {dataAdded.length > 0 && (
            <DatasetSection
              title="Data Added"
              description="These datasets will be added to your analysis canvas based on the filters you selected."
              datasets={dataAdded}
              isLockedSection={false}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 bg-white">
          {/* Ignore data locked checkbox */}
          {dataLocked.length > 0 && (
            <div className="flex items-center gap-2 my-4">
              <input
                type="checkbox"
                id="ignoreLocked"
                checked={ignoreDataLocked}
                onChange={(e) => setIgnoreDataLocked(e.target.checked)}
                className="w-3 h-3 cursor-pointer accent-teal-500"
              />
              <label
                htmlFor="ignoreLocked"
                className="text-sm text-txtBody cursor-pointer font-['Plus_Jakarta_Sans']"
              >
                Ignore data locked
              </label>
            </div>
          )}
          <div className="flex gap-3 justify-end items-center">
            <Button
              variant="outline"
              onClick={handleCloseModalConfirmation}
              className="flex-1 h-[44px] text-teal-500 border-teal-500 font-semibold text-[14px] hover:bg-teal-50 rounded-xl"
            >
              Back to Edit
            </Button>
            <Button
              disabled={isDisabled || isCheckingLimit || isInputRowsOverLimit}
              onClick={handleConfirm}
              className="flex-1 h-[44px] text-white font-semibold text-[14px] rounded-xl"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
